import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Skeleton,
} from '@mui/material';
import { Podcasts, CalendarMonth, BarChart as BarChartIcon, Campaign, Download, Announcement, EventNote, FormatListBulleted, RocketLaunch, CheckCircle, Insights, DynamicFeed, Movie } from '@mui/icons-material';
import ReleaseCalendar from './ReleaseCalendar';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import { megaphoneAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';
import CTACampaignsTab from './megaphone/CTACampaignsTab';
import FooterTemplatesTab from './megaphone/FooterTemplatesTab';
import PublishEpisodeTab from './megaphone/PublishEpisodeTab';
import PublishedEpisodesTab from './megaphone/PublishedEpisodesTab';
import MumSetupTab from './megaphone/MumSetupTab';
import ShortsBatchTab from './megaphone/ShortsBatchTab';
import PublishActivityFeed from './megaphone/PublishActivityFeed';
import LiveTrendingPanel from './megaphone/LiveTrendingPanel';
import { useUrlIntState } from '../hooks/useUrlState';
import FlightpathDashboard from './FlightpathDashboard';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const fmtDur = (s: number | null | undefined) => {
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

interface MegProps { mumDeep?: { site: string; nonce: number } | null }

const MUM_TAB_INDEX = 10;

const MegaphoneDashboard: React.FC<MegProps> = ({ mumDeep }) => {
  // Active tab index mirrored in the URL as ?meg_tab=N so a refresh
  // lands on the same tab instead of bouncing back to Episodes.
  const [active, setActive] = useUrlIntState('meg_tab', 0);

  // Deep-link from the dead-session banner: jump straight to MUM Setup.
  React.useEffect(() => {
    if (mumDeep) setActive(MUM_TAB_INDEX);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mumDeep?.nonce]);
  const [selectedShow, setSelectedShow] = useState<string>('');

  const { data: overview } = useQuery({ queryKey: ['meg-overview'], queryFn: megaphoneAPI.getOverview, staleTime: 60000 });
  const { data: shows } = useQuery({ queryKey: ['meg-shows'], queryFn: megaphoneAPI.getShows, staleTime: 60000 });
  const { data: seasons } = useQuery({ queryKey: ['meg-seasons', selectedShow], queryFn: () => megaphoneAPI.getSeasons(selectedShow || undefined), staleTime: 60000 });
  const { data: cadence } = useQuery({ queryKey: ['meg-cadence', selectedShow], queryFn: () => megaphoneAPI.getCadence(selectedShow || undefined), staleTime: 60000 });
  const { data: adStats } = useQuery({ queryKey: ['meg-ads', selectedShow], queryFn: () => megaphoneAPI.getAdStats(selectedShow || undefined), staleTime: 60000 });
  const { data: episodes } = useQuery({ queryKey: ['meg-episodes', selectedShow], queryFn: () => megaphoneAPI.getEpisodes({ show_id: selectedShow || undefined, limit: 20 }), staleTime: 60000 });
  // downloads + demographics hooks removed — Downloads tab now renders
  // <FlightpathDashboard embedded/> directly, so those queries were dead.

  // Pick first show by default
  React.useEffect(() => {
    if (shows?.shows?.length && !selectedShow) {
      setSelectedShow(shows.shows[0].id);
    }
  }, [shows, selectedShow]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Episode catalog, release cadence, ad metadata_">
          MEGAPHONE :: PODCAST CONSOLE
        </SectionTitle>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'EPISODES', value: fmt(overview?.episode_count), sub: `across ${overview?.show_count || 0} feed(s)` },
          { label: 'SEASONS', value: fmt(overview?.season_count) },
          { label: 'AVG DURATION', value: fmtDur(overview?.avg_duration_seconds) },
          { label: 'CATALOG SPAN', value: overview?.first_episode ? `${overview.first_episode} → ${overview.latest_episode}` : '—' },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 3 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
              {kpi.sub && <Typography variant="caption" color="textSecondary">{kpi.sub}</Typography>}
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<Podcasts />} label="Episodes" />
          <Tab icon={<EventNote />} label="Release calendar" />
          <Tab icon={<CalendarMonth />} label="Seasons" />
          <Tab icon={<BarChartIcon />} label="Cadence" />
          <Tab icon={<Campaign />} label="Ad Slots" />
          <Tab icon={<Download />} label="Downloads" />
          <Tab icon={<Announcement />} label="CTA Campaigns" />
          <Tab icon={<FormatListBulleted />} label="Footer Templates" />
          <Tab icon={<RocketLaunch />} label="Publish" />
          <Tab icon={<CheckCircle />} label="Published" />
          <Tab icon={<Insights />} label="MUM (live metrics)" />
          <Tab icon={<DynamicFeed />} label="All activity" />
          <Tab icon={<Movie />} label="Shorts" />
        </Tabs>
      </Paper>

      {/* Episodes tab */}
      <TabPanel value={active} index={0}>
        <LiveTrendingPanel />
        <Panel label="RECENT EPISODES">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>SEASON</TableCell>
                  <TableCell>EP</TableCell>
                  <TableCell>PUBLISHED</TableCell>
                  <TableCell>DURATION</TableCell>
                  <TableCell>ADS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(episodes?.episodes || []).map((ep: any) => (
                  <TableRow key={ep.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 400 }}>{ep.title}</Typography>
                      {ep.subtitle && <Typography variant="caption" color="textSecondary" noWrap sx={{ display: 'block', maxWidth: 400 }}>{ep.subtitle}</Typography>}
                    </TableCell>
                    <TableCell>{ep.season_number ?? '—'}</TableCell>
                    <TableCell>{ep.episode_number ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{ep.publish_date ? new Date(ep.publish_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{fmtDur(ep.duration_seconds)}</TableCell>
                    <TableCell>
                      {ep.ad_free ? (
                        <Chip label="AD FREE" size="small" color="info" />
                      ) : (
                        <Typography variant="caption">{ep.pre_count || 0} pre / {ep.post_count || 0} post</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Showing {episodes?.episodes?.length || 0} of {episodes?.total || 0} episodes
          </Typography>
        </Panel>
      </TabPanel>

      {/* Release calendar */}
      <TabPanel value={active} index={1}>
        <MegaphoneCalendarTab />
      </TabPanel>

      {/* Seasons tab */}
      <TabPanel value={active} index={2}>
        <Panel label="SEASON BREAKDOWN">
          {(seasons?.seasons || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={seasons.seasons} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="season_number" label={{ value: 'Season', position: 'bottom' }} />
                <YAxis />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Bar dataKey="episodes" fill="#00ff41" name="Episodes" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SEASON</TableCell>
                  <TableCell align="right">EPISODES</TableCell>
                  <TableCell>DATES</TableCell>
                  <TableCell align="right">AVG DURATION</TableCell>
                  <TableCell align="right">TOTAL TIME</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(seasons?.seasons || []).map((s: any) => (
                  <TableRow key={s.season_number} hover>
                    <TableCell><Typography fontWeight={600}>S{s.season_number}</Typography></TableCell>
                    <TableCell align="right">{s.episodes}</TableCell>
                    <TableCell>{s.first_episode} → {s.latest_episode}</TableCell>
                    <TableCell align="right">{fmtDur(s.avg_duration_seconds)}</TableCell>
                    <TableCell align="right">{fmtDur(s.total_duration_seconds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Cadence tab */}
      <TabPanel value={active} index={3}>
        <Panel label="RELEASE CADENCE (MONTHLY)">
          {(cadence?.months || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={cadence.months} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={(d: string) => d?.substring(0, 7)} />
                <YAxis />
                <Tooltip
                  labelFormatter={(d: string) => d?.substring(0, 7)}
                  formatter={(v: number, name: string) => [fmt(v), name === 'episodes' ? 'Episodes' : 'Total seconds']}
                />
                <Area type="monotone" dataKey="episodes" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
        </Panel>
      </TabPanel>

      {/* Ad Slots tab */}
      <TabPanel value={active} index={4}>
        <Panel label="AD INSERTION OVERVIEW">
          {adStats ? (
            <Grid container spacing={3}>
              {[
                { label: 'TOTAL EPISODES', value: fmt(adStats.total_episodes) },
                { label: 'AD-FREE', value: fmt(adStats.ad_free_count) },
                { label: 'WITH PRE-ROLL', value: fmt(adStats.with_preroll) },
                { label: 'WITH POST-ROLL', value: fmt(adStats.with_postroll) },
                { label: 'AVG PRE-ROLL', value: String(adStats.avg_preroll ?? 0) },
                { label: 'AVG POST-ROLL', value: String(adStats.avg_postroll ?? 0) },
              ].map((s) => (
                <Grid size={{ xs: 6, md: 2 }} key={s.label}>
                  <Typography variant="caption" color="textSecondary">{s.label}</Typography>
                  <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                </Grid>
              ))}
            </Grid>
          ) : <Skeleton variant="rectangular" height={120} />}
        </Panel>
      </TabPanel>

      {/* CTA Campaigns tab */}
      <TabPanel value={active} index={6}>
        <CTACampaignsTab />
      </TabPanel>

      {/* Downloads tab — real Flightpath S3 data */}
      <TabPanel value={active} index={5}>
        <FlightpathDashboard embedded />
      </TabPanel>

      {/* Footer Templates tab — bulk-rewrite the canonical SL/MB footer */}
      <TabPanel value={active} index={7}>
        <FooterTemplatesTab />
      </TabPanel>

      {/* Publish wizard — new episode draft + Megaphone create */}
      <TabPanel value={active} index={8}>
        <PublishEpisodeTab />
      </TabPanel>

      {/* Published index — one row per live episode + every platform link */}
      <TabPanel value={active} index={9}>
        <PublishedEpisodesTab />
      </TabPanel>

      {/* MUM (private Megaphone API) — cookie setup + status + run-now */}
      <TabPanel value={active} index={10}>
        <MumSetupTab mumDeep={mumDeep} />
      </TabPanel>

      {/* Unified feed — everything published through the app (Patreon + episodes) */}
      <TabPanel value={active} index={11}>
        <PublishActivityFeed />
      </TabPanel>

      {/* Bulk shorts: ingest a batch of vertical clips, schedule them on a
          cadence, fan out per platform. Appended as index 12 so existing
          ?meg_tab=N links keep resolving to the same tabs. */}
      <TabPanel value={active} index={12}>
        <ShortsBatchTab />
      </TabPanel>
    </Container>
  );
};

export default MegaphoneDashboard;

// Palette cycled over the distinct shows so every show gets its own color.
const SHOW_PALETTE = ['#00ff41', '#00ffff', '#ffb000', '#ff6600', '#ff3344', '#c774ff'];

const MegaphoneCalendarTab: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['meg-calendar', 24],
    queryFn: () => megaphoneAPI.getCalendar(24),
    staleTime: 5 * 60_000,
  });
  const shows: { id: string; title: string }[] = data?.shows || [];
  const legend = shows.map((s, i) => ({
    type: s.id,
    label: s.title,
    color: SHOW_PALETTE[i % SHOW_PALETTE.length],
  }));
  const colorOf = (showId: string) =>
    legend.find((l) => l.type === showId)?.color || '#5aaa5a';
  const events = (data?.events || []).map((e: any) => ({
    date: e.date,
    title: e.title,
    color: colorOf(e.type),
    type: e.type,
    subtitle: e.show_name,
  }));
  return (
    <Box sx={{ p: 1 }}>
      <ReleaseCalendar
        heading={`> ${events.length} episodes across ${shows.length} show(s), last 24 months`}
        events={events}
        legend={legend}
      />
    </Box>
  );
};
